import { CmsAttachedBlocks } from '@longy/common/cms'
import { useTranslation } from 'react-i18next'

import { resolveProgramCardContent } from '../../utils/programCard'
import { compactRichTextClassName } from '../../utils/richText'
import { AttachedBlocks, useAttachedBlocks } from '../cms/AttachedBlocks'

interface ProgramCardProps {
    name: string
    price: number
    originalPrice?: number | null
    descriptionHtml: string
    cmsBlocks?: CmsAttachedBlocks
    colorBg: string
    colorRing: string
    ctaHref?: string
    checkoutHref?: string
    detailsHref?: string
}

const formatPrice = (price: number) => {
    return (
        new Intl.NumberFormat('hu-HU', {
            style: 'decimal',
            maximumFractionDigits: 0,
        }).format(price) + ' Ft'
    )
}

export const ProgramCard = ({
    name,
    price,
    originalPrice,
    descriptionHtml,
    cmsBlocks,
    colorBg,
    colorRing,
    ctaHref,
    checkoutHref,
    detailsHref,
}: ProgramCardProps) => {
    const { t } = useTranslation()
    const { hasBody, hasDetails } = resolveProgramCardContent({
        compactBlocks: useAttachedBlocks(cmsBlocks, true),
        blocks: useAttachedBlocks(cmsBlocks),
        descriptionHtml,
    })
    const hasDiscount =
        typeof originalPrice === 'number' && originalPrice > 0 && originalPrice > price

    return (
        <div className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
            <div className="flex flex-col items-center gap-4 px-8 pt-10">
                <div className={`h-16 w-16 rounded-full ${colorBg} ring-4 ${colorRing}`} />
                <div className="text-center">
                    <h3 className="text-xl font-bold uppercase tracking-wide text-brand-dark">
                        {name}
                    </h3>
                </div>
                <div className="flex flex-col items-center gap-1">
                    {hasDiscount && (
                        <span className="text-base font-medium text-brand-gray line-through">
                            {formatPrice(originalPrice)}
                        </span>
                    )}
                    <span className="text-3xl font-bold text-brand-dark">{formatPrice(price)}</span>
                </div>
            </div>

            {hasBody && (
                <div className="px-8 py-6">
                    <AttachedBlocks compact blocks={cmsBlocks} className="mb-4" />
                    {descriptionHtml && (
                        <div
                            className={compactRichTextClassName}
                            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                        />
                    )}
                </div>
            )}

            <div className="mt-auto border-t border-slate-100 px-8 py-6">
                {checkoutHref && (
                    <a
                        href={checkoutHref}
                        className="block w-full rounded-full bg-primary py-3 text-center font-bold text-white transition-colors hover:bg-brand-yellow"
                    >
                        {t('frontend.lifestylePrograms.apply')}
                    </a>
                )}
                {ctaHref && (
                    <a
                        href={ctaHref}
                        className={`${checkoutHref ? 'mt-3' : ''} block w-full rounded-full border-2 border-primary py-3 text-center font-bold text-primary transition-colors hover:bg-primary/5`}
                    >
                        {t('frontend.lifestylePrograms.apply')}
                    </a>
                )}
                {detailsHref && hasDetails && (
                    <a
                        href={detailsHref}
                        className="mt-3 block w-full rounded-full border-2 border-primary py-3 text-center font-bold text-primary transition-colors hover:bg-primary/5"
                    >
                        {t('frontend.catalog.details')}
                    </a>
                )}
            </div>
        </div>
    )
}
